// Debug mode logger — owner-toggleable, off by default, auto-disabled
// after 60 minutes regardless. Toggle lives at /settings/owner/developer
// (gate at router level via notFound()) and only Collin's account can
// flip it (see lib/owner-debug-config.js).
//
// Contract:
//   debug.log(module, action, payload, options)
//
//   - When the runtime debug flag is OFF → no-op (silent return).
//   - When ON → console.debug with module prefix + per-module color
//     (client-side) or plain console.debug (server-side).
//   - SERVER-SIDE additionally emits a vault_debug_trace signal at trace
//     severity so server-side narration shows up alongside client logs in
//     /owner/radio (with the "Show trace events" toggle on).
//
// Redaction (security default-on; override with options.uncensored = true):
//   - Fields named "content" or "text" → replaced with "[redacted: N chars]"
//   - Any string field longer than REDACT_LEN_THRESHOLD chars → same
//   - Recursive into nested objects + arrays
//   - Non-string fields pass through unchanged
//
// State management:
//   - Runtime flag is module-level (set by setDebugEnabled) so callers
//     don't have to thread it through every debug.log call.
//   - Client: AuthProvider calls setDebugEnabled(getEffectiveDebugMode(profile))
//     once on profile load + on every refresh.
//   - Server: chat route + other request handlers call setDebugEnabled
//     at request entry (one of N places where profile is fetched).
//
// Stale flag rule:
//   - Reads via getEffectiveDebugMode return false when enabled_at is
//     more than 60 min old.
//   - When the toggle endpoint detects a stale flag on read, it lazy-
//     clears the row (UPDATE) and emits vault_debug_mode_auto_disabled.

const _isServer = typeof window === "undefined";

// last_reviewed: 2026-04-26
export const DEBUG_MODE_TTL_MS = 60 * 60 * 1000;

// last_reviewed: 2026-04-26
export const REDACT_LEN_THRESHOLD = 80;

const REDACT_FIELDS = new Set(["content", "text"]);

// Per-module CSS color for client-side console.debug coloring. Server
// logs are uncolored (Vercel/terminal don't render CSS).
const MODULE_COLORS = {
  vault_storage: "color: #0a8;font-weight:bold",
  vault_binary: "color: #a80;font-weight:bold",
  vault_ledger: "color: #a08;font-weight:bold",
  vault_pulse: "color: #088;font-weight:bold",
  vault_source: "color: #f80;font-weight:bold",
  chat_route: "color: #08f;font-weight:bold",
  default: "color: #888;font-weight:bold",
};

// ── Pure helpers (testable) ───────────────────────────────────────────

// Replace sensitive payload fields with a length-redacted placeholder.
// Non-recursive caller path is fine for primitives + arrays of primitives.
export function redactPayload(payload, options = {}) {
  if (options?.uncensored) return payload;
  return _redact(payload);
}

function _redact(value) {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) return value.map((v) => _redact(v));
  if (typeof value === "object") {
    const out = {};
    for (const [key, v] of Object.entries(value)) {
      if (REDACT_FIELDS.has(key)) {
        out[key] = `[redacted: ${typeof v === "string" ? v.length + " chars" : typeof v}]`;
      } else if (typeof v === "string" && v.length > REDACT_LEN_THRESHOLD) {
        out[key] = `[redacted: ${v.length} chars]`;
      } else if (v !== null && typeof v === "object") {
        out[key] = _redact(v);
      } else {
        out[key] = v;
      }
    }
    return out;
  }
  // Strings at top level still get length-redacted if long
  if (typeof value === "string" && value.length > REDACT_LEN_THRESHOLD) {
    return `[redacted: ${value.length} chars]`;
  }
  return value;
}

// Stale check — debug toggle auto-disables after DEBUG_MODE_TTL_MS.
export function isDebugFlagStale(enabledAt, now = Date.now()) {
  if (!enabledAt) return true;
  const t = typeof enabledAt === "string" ? new Date(enabledAt).getTime() : enabledAt;
  if (!t || Number.isNaN(t)) return true;
  return (now - t) > DEBUG_MODE_TTL_MS;
}

// Compute effective debug mode from a profile-shaped input.
// Profile must have debug_mode_enabled (bool) + debug_mode_enabled_at
// (timestamp or null). Returns true only when enabled AND not stale.
export function getEffectiveDebugMode(profile, now = Date.now()) {
  if (!profile || profile.debug_mode_enabled !== true) return false;
  if (isDebugFlagStale(profile.debug_mode_enabled_at, now)) return false;
  return true;
}

// ── Runtime state + emit (impure) ─────────────────────────────────────

const _state = { enabled: false };

export function setDebugEnabled(enabled) {
  _state.enabled = !!enabled;
}

export function isDebugEnabled() {
  return _state.enabled;
}

// debug.log — main entry point. No-op when flag off.
export const debug = {
  log(module, action, payload, options = {}) {
    if (!_state.enabled) return;
    const safeModule = String(module || "default");
    const safeAction = String(action || "");
    const redacted = redactPayload(payload, options);

    if (_isServer) {
      console.debug(`[${safeModule}] ${safeAction}`, redacted);
      _emitServerTrace(safeModule, safeAction, redacted);
    } else {
      const color = MODULE_COLORS[safeModule] || MODULE_COLORS.default;
      console.debug(`%c[${safeModule}] ${safeAction}`, color, redacted);
    }
  },
};

// Server-side echo to Radio at trace severity. Lazy-imported so client
// bundles don't pull signal-server. Fire-and-forget; failures don't
// surface to the caller.
function _emitServerTrace(module, action, redactedPayload) {
  if (!_isServer) return;
  import("./signal-server").then(({ emitServerSignal }) => {
    emitServerSignal(null, "vault_debug_trace", "trace", {
      module,
      action,
      payload: redactedPayload,
    });
  }).catch(() => { /* fire-and-forget */ });
}
